import { memo, useEffect, useRef } from "react";
import CardMediaMUI from "@mui/material/CardMedia";
import { useInView } from "react-intersection-observer";
import Skeleton from "@mui/material/Skeleton";
import { formatUrlMedia, getExtensionFile } from "../../utils/string";
import { VIDEO_FORMATS } from "../../utils/constants";

const NOOP = () => {};

const applyInlineVideoPlayback = (element) => {
  if (!element) return;
  element.playsInline = true;
  element.setAttribute("playsinline", "");
  element.setAttribute("webkit-playsinline", "true");

  if (element.dataset.inlinePlaybackBound === "true") return;
  element.dataset.inlinePlaybackBound = "true";

  const exitNativeFullscreen = () => {
    if (typeof element.webkitExitFullscreen === "function") {
      element.webkitExitFullscreen();
    }
  };

  element.addEventListener("webkitbeginfullscreen", exitNativeFullscreen);
};

const resolveCardMediaElementType = ({ mediaType, extension }) => {
  if (mediaType === "video") return "video";
  if (mediaType === "image") return "img";

  return VIDEO_FORMATS.includes(extension) ? "video" : "img";
};

let propsTypeVideo = [
  "controls",
  "autoplay",
  "autoPlay",
  "loop",
  "controlsList",
  "muted",
  "preload",
  "playsInline",
  "playsinline",
];
let propsTypeImage = ["draggable", "alt"];

const CardMedia = memo(function CardMedia({
  propsType = {},
  sx = {},
  sxImage = {},
  sxVideo = {},
  url,
  mediaType,
  onFailedLoadUrl = () => {},
  onMediaLoad = NOOP,
  onClick,
  eager = false,
  releaseWhenHidden = false,
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px 0px",
    initialInView: eager,
  });
  const extension = getExtensionFile(url);
  url = formatUrlMedia(url);
  const type = resolveCardMediaElementType({ mediaType, extension });
  const mediaRef = useRef(null);

  const getPropsType = () => {
    const propsTypeKeysFiltered = Object.keys(propsType).filter((propsType) => {
      if (type === "video" && propsTypeVideo.includes(propsType)) {
        return true;
      }

      if (type === "img" && propsTypeImage.includes(propsType)) {
        return true;
      }

      if (
        !propsTypeImage.includes(propsType) &&
        !propsTypeVideo.includes(propsType)
      ) {
        return true;
      }

      return false;
    });

    const propsTypeData = {};

    propsTypeKeysFiltered.forEach((key) => {
      propsTypeData[key] = propsType[key];
    });

    return propsTypeData;
  };

  const propsTypeFiltered = getPropsType();

  const propsMedia =
    type === "video"
      ? {
          component: type,
          controls: true,
          autoplay: false,
          loop: false,
          preload: "metadata",
          src: url,
          ...propsTypeFiltered,
          playsInline: true,
        }
      : {
          component: type,
          src: url,
          ...propsTypeFiltered,
        };

  useEffect(() => {
    if (type !== "video" || !inView) return;
    const videoElement = mediaRef.current;
    if (!videoElement || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoElement) return;
        if (entry.isIntersecting) {
          applyInlineVideoPlayback(videoElement);
          if (propsMedia.autoplay) {
            videoElement.play().catch(() => {});
          }
          return;
        }
        videoElement.pause();
      },
      {
        threshold: 0.15,
        rootMargin: "80px 0px",
      }
    );

    observer.observe(videoElement);

    return () => {
      observer.disconnect();
    };
  }, [inView, propsMedia.autoplay, type, url]);

  useEffect(() => {
    if (type !== "video") return;
    const videoElement = mediaRef.current;
    if (!videoElement || typeof propsMedia.muted !== "boolean") return;
    videoElement.muted = propsMedia.muted;
  }, [type, url, propsMedia.muted]);

  // Free the video decoder once the element leaves the DOM (released by the
  // parent while off screen, or unmounted).
  useEffect(() => {
    if (!releaseWhenHidden || type !== "video" || !inView) return;
    const videoElement = mediaRef.current;
    if (!videoElement) return;

    return () => {
      if (videoElement.isConnected) return;
      videoElement.pause();
      videoElement.removeAttribute("src");
      videoElement.load();
    };
  }, [releaseWhenHidden, type, inView, url]);

  useEffect(() => {
    if (!inView) return;
    const mediaElement = mediaRef.current;
    if (!mediaElement) return;

    if (
      type === "img" &&
      mediaElement.complete &&
      mediaElement.naturalWidth > 0
    ) {
      onMediaLoad();
      return;
    }

    if (type === "video" && mediaElement.readyState >= 2) {
      onMediaLoad();
    }
  }, [inView, onMediaLoad, type, url]);

  if (!eager && !inView) {
    return (
      <Skeleton
        ref={ref}
        variant="rounded"
        sx={{
          width: "100%",
          height: "100%",
        }}
      />
    );
  }

  // if (type === "video" && videoComponent === "player") {
  //   return <VideoPlayer url={url} sx={{ ...sx, ...sxVideo }} {...propsMedia} />;
  // }

  if (type === "video") {
    return (
      <CardMediaMUI
        {...propsMedia}
        ref={(node) => {
          mediaRef.current = node;
          applyInlineVideoPlayback(node);
        }}
        playsInline
        sx={{ ...sx, ...sxVideo }}
        onLoadedData={onMediaLoad}
        onError={onFailedLoadUrl}
        onClick={onClick}
      />
    );
  }
  return (
    <CardMediaMUI
      {...propsMedia}
      ref={mediaRef}
      sx={{ ...sx, ...sxImage }}
      onLoad={onMediaLoad}
      onError={onFailedLoadUrl}
      onClick={onClick}
    />
  );
});

export default CardMedia;
